import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  type InputProps,
} from "@/components/ui";
import { forwardRef } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";
import { NumericFormat, type NumericFormatProps } from "react-number-format";

export interface CurrencyFormFieldProps {
  label?: string;
  className?: string;
  register: UseFormRegisterReturn;
  disabled?: boolean;
  placeholder?: string;
  dataTestId?: string;
  fixedDecimalScale?: boolean;
  onChange?: (value: string) => void;
}

const NumericFormatInput = forwardRef<
  HTMLInputElement,
  Omit<NumericFormatProps<InputProps>, "getInputRef">
>((props, ref) => {
  return <NumericFormat {...props} getInputRef={ref} />;
});
NumericFormatInput.displayName = "NumericFormatInput";

export const CurrencyFormField = ({
  label,
  className,
  register,
  disabled,
  placeholder,
  dataTestId,
  fixedDecimalScale = false,
  onChange,
}: CurrencyFormFieldProps) => {
  const form = useFormContext();
  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        const { ref: fieldRef, ...fieldProps } = field;
        return (
          <FormItem className={className}>
            <div className="flex">
              {label && <FormLabel>{label}</FormLabel>}
              <FormMessage
                data-testid="error-message"
                className="grow text-right"
              />
            </div>
            <div className="relative">
              <span className="text-muted-foreground absolute inset-y-0 left-0 flex items-center pl-3 text-sm">
                $
              </span>
              <FormControl>
                <NumericFormatInput
                  disabled={disabled}
                  data-testid={dataTestId}
                  placeholder={placeholder}
                  aria-label={label}
                  defaultValue={field.value}
                  className="pl-8"
                  thousandSeparator=","
                  customInput={Input}
                  decimalScale={2}
                  fixedDecimalScale={fixedDecimalScale}
                  {...fieldProps}
                  ref={fieldRef}
                  onChange={() => {}} // Prevent NumericFormat from emitting the formatted string
                  onValueChange={({ value }, sourceInfo) => {
                    if (!sourceInfo.event) return;
                    form.setValue(field.name, value, { shouldDirty: true });
                    field.onChange(value);
                    onChange?.(value);
                  }}
                />
              </FormControl>
            </div>
          </FormItem>
        );
      }}
    />
  );
};
